/**
 * Pure logic behind the "what would move your score" panel.
 *
 * Kept out of the component so the part that can actually be wrong is testable
 * without rendering anything. The presentation lives in
 * `components/ScoreLevers.jsx`.
 *
 * The backend (`backend/counterfactual.py`) owns the arithmetic and the
 * ordering. Nothing here recomputes a gain — doing so would create a second
 * source of truth that could silently disagree with the score.
 */

/** The largest gain in the set, which every bar is scaled against. */
export function maxGain(levers) {
    return (levers || []).reduce(
        (m, l) => Math.max(m, Number(l?.gain_if_perfect) || 0),
        0
    );
}

/**
 * Bar width as a percentage.
 *
 * Scaled to the largest lever rather than a fixed divisor. With a fixed one the
 * top few levers saturate at 100% and the ranking — the only reason the panel
 * exists — becomes invisible.
 *
 * Positive gains get a 4% floor so the smallest one still reads as a bar rather
 * than as nothing. Zero stays zero: a signal that cannot move the score must
 * not be drawn as though it could.
 */
export function barPercent(gain, max) {
    const g = Number(gain);
    if (!Number.isFinite(g) || g <= 0 || !max || max <= 0) return 0;
    return Math.max(4, Math.round((g / max) * 100));
}

/**
 * A lever that cannot move the score: either it carries no weight in this
 * scorecard, or it is already at maximum. Both are shown, greyed, with the
 * backend's note. Hiding them would drop the most useful thing the panel says —
 * that growing a following is worth nothing under this scorecard.
 */
export function isInertLever(lever) {
    const gain = Number(lever?.gain_if_perfect) || 0;
    return (Number(lever?.weight) || 0) === 0 || gain <= 0;
}

/** A tier unlock only counts when the score actually moves to reach it. */
export function unlocksTier(lever) {
    return Boolean(lever?.unlocks_tier) && (Number(lever?.gain_if_perfect) || 0) > 0;
}
